import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { isEqual, pickBy } from 'lodash';
import { type DudaConfiguration } from '~api/admin/businesses/dudaConfiguration';
import { Button, ButtonVariant } from 'components/common/buttons/Button';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import styles from './EditDudaConfiguration.module.scss';
import { useAddDudaConfiguration } from './mutations/useAddDudaConfiguration';
import { useEditDudaConfiguration } from './mutations/useEditDudaConfiguration';
import { useGetDudaConfiguration } from './queries/useGetDudaConfiguration';
import { forceDudaSync } from 'api/admin/businesses/businesses';
import cx from 'classnames';

interface EditDudaConfigurationProps {
  businessId: number;
  onClose: () => void;
}

export const EditDudaConfiguration = ({ businessId, onClose }: EditDudaConfigurationProps) => {
  const { data, isLoading: isGetDudaConfigsLoading } = useGetDudaConfiguration(businessId);
  const isNew = !data;

  const initialDudaConfigs: DudaConfiguration = {
    dudaSiteName: data?.dudaSiteName || '',
  };

  const [dudaConfigs, setDudaConfigs] = useState(initialDudaConfigs);

  const { mutateAsync: addDudaConfiguration, isLoading: isAddDudaConfigsLoading } =
    useAddDudaConfiguration();
  const { mutateAsync: editDudaConfiguration, isLoading: isEditDudaConfigsLoading } =
    useEditDudaConfiguration();

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

  useEffect(() => {
    setDudaConfigs(initialDudaConfigs);
  }, [data]);

  const handleInputChange =
    (field: keyof DudaConfiguration) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setDudaConfigs((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSave = async () => {
    if (isNew) {
      await addDudaConfiguration({ businessId, data: dudaConfigs });
    } else {
      const updatedFields = pickBy(dudaConfigs, (value, key) => initialDudaConfigs[key] !== value);
      if (Object.keys(updatedFields).length > 0) {
        await editDudaConfiguration({ businessId, data: updatedFields });
      }
    }
    onClose();
  };

  const handleForceSync = async () => {
    try {
      setIsSyncing(true);
      setSyncError(null);
      setSyncSuccess(null);
      await forceDudaSync({ businessId });
      setSyncSuccess('Duda sync initiated successfully!');
    } catch (error) {
      setSyncError('Something went wrong while forcing sync');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className={styles.editDudaConfigurationContainer}>
      <h1 className={styles.editDudaConfigurationContainerHeader}>
        {`${isNew ? 'Add' : 'Edit'} Duda Configuration`}
      </h1>

      {isGetDudaConfigsLoading && <CentsLoader />}
      {!isGetDudaConfigsLoading && (
        <div className={styles.editDudaConfigurationContainerContent}>
          <TextField
            label="Duda Site Name"
            variant="outlined"
            value={dudaConfigs.dudaSiteName}
            onChange={handleInputChange('dudaSiteName')}
          />
        </div>
      )}

      {syncError && <p className="error-text">{syncError}</p>}
      {syncSuccess && <p className="success-text">{syncSuccess}</p>}

      <div className={styles.buttonContainer}>
        <button
          className={cx(styles.button, styles.forceSyncButton, {
            [styles.disabled]: isSyncing || isGetDudaConfigsLoading || !data?.dudaSiteName?.trim()
          })}
          disabled={isSyncing || isGetDudaConfigsLoading || !data?.dudaSiteName?.trim()}
          onClick={handleForceSync}
        >
          Force Sync
        </button>

        <button
          className={cx(styles.button, styles.saveButton, {
            [styles.disabled]: isAddDudaConfigsLoading || 
              isEditDudaConfigsLoading || 
              isEqual(dudaConfigs, initialDudaConfigs) ||
              !dudaConfigs.dudaSiteName.trim()
          })}
          disabled={
            isAddDudaConfigsLoading ||
            isEditDudaConfigsLoading ||
            isEqual(dudaConfigs, initialDudaConfigs) ||
            !dudaConfigs.dudaSiteName.trim()
          }
          onClick={handleSave}
        >
          {`${isNew ? 'Add' : 'Save'}`}
        </button>
      </div>
    </div>
  );
};
